import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/hooks/use-auth";
import AdminGuard from "@/components/AdminGuard";
import Layout from "@/components/Layout";
import PageViewTracker from "@/components/PageViewTracker";
import GAInit from "@/components/GAInit";

const Maintenance = lazy(() => import("./pages/Maintenance"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminPage = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const Index = lazy(() => import("./pages/Index"));
const Domains = lazy(() => import("./pages/Domains"));
const DomainDetails = lazy(() => import("./pages/DomainDetails"));
const Sales = lazy(() => import("./pages/Sales"));
const SearchPage = lazy(() => import("./pages/Search"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const SellDomain = lazy(() => import("./pages/SellDomain"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ExtensionPage = lazy(() => import("./pages/ExtensionPage"));
const Compare = lazy(() => import("./pages/Compare"));
const About = lazy(() => import("./pages/About"));

const BulkInquiry = lazy(() => import("./pages/BulkInquiry"));
const Escrow = lazy(() => import("./pages/Escrow"));
const ConfirmSearch = lazy(() => import("./pages/ConfirmSearch"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ThemeProvider>
    
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <GAInit />
          <PageViewTracker />
          <Suspense fallback={<div className="flex justify-center items-center min-h-screen"><div className="h-8 w-8 border-2 border-muted-foreground/20 border-t-foreground rounded-full animate-spin" /></div>}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminGuard><AdminPage /></AdminGuard>} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Index />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/domains/:slug" element={<DomainDetails />} />
              <Route path="/sales" element={<Sales />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/sell-domain" element={<SellDomain />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/category/:category" element={<CategoryPage />} />
              <Route path="/extension/:tld" element={<ExtensionPage />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/about" element={<About />} />
              
              <Route path="/bulk-inquiry" element={<BulkInquiry />} />
              <Route path="/escrow" element={<Escrow />} />
              <Route path="/confirm-search" element={<ConfirmSearch />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
    
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
